import { EmailOutlined, PhoneOutlined, WhatsApp } from "@mui/icons-material";
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { useMemo, useState} from "react";
import { useTranslation } from "react-i18next";
import { CONTACT_EMAIL } from "config/contact";
import Meta from "../Meta/Meta";
import { contactUsAction } from "../../redux/Actions/contactUsAction";

export default function ContactPage() {
  const { t, i18n } = useTranslation(["ui"]);
  const dir = i18n.dir();

  const dispatch = useDispatch();
  const { success } = useSelector((state) => state.contactUs);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const emailRegex = /[^\s@]+@[^\s@]+\.[^\s@]+/;

  const validateField = (field, value) => {
    switch (field) {
      case "name":
        return value.trim() ? "" : t("contact.form.validation.nameRequired");
      case "email":
        if (!value.trim()) return t("contact.form.validation.emailRequired");
        return emailRegex.test(value)
          ? ""
          : t("contact.form.validation.emailInvalid");
      case "phone":
        if (!value) return ""; // optional
        return /[\d\s+()-]{6,}/.test(value)
          ? ""
          : t("contact.form.validation.phoneInvalid");
      case "message":
        return value.trim() ? "" : t("contact.form.validation.messageRequired");
      default:
        return "";
    }
  };

  const validateAll = (state) => {
    const next = {
      name: validateField("name", state.name),
      email: validateField("email", state.email),
      phone: validateField("phone", state.phone),
      message: validateField("message", state.message),
    };
    setErrors(next);
    return Object.values(next).every((e) => !e);
  };

  const contactEmail = CONTACT_EMAIL;
  const whatsappNumber = "+49 1512 5368005";
  const whatsappHref = useMemo(
    () => `https://wa.me/${whatsappNumber.replace(/[^\d]/g, "")}`,
    [whatsappNumber]
  );

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const handleBlur = (field) => (e) => {
    const value = e.target.value;
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = validateAll(form);
    if (!ok) return;
    await dispatch(contactUsAction(form));
    setSubmitting(false);
    // reset form only if success
    if (success) {
      setForm({ name: "", email: "", phone: "", message: "" });
    }
  };

  return (
    <>
      <Meta
        title={t("contact.metaTitle")}
        description={t("contact.metaDescription")}
        keywords={t("contact.metaKeywords")}
      />
      <Box sx={{ mb: 10 }}>
        {/* Hero Banner  */}
        <Box
          sx={{
            position: "relative",
            mb: { xs: 5, md: 8 },
            height: { xs: 260, md: 520 },
            backgroundImage:
              "linear-gradient(180deg, rgba(0,0,0,.65), rgba(0,0,0,.65)), url('/home-heidelberg-bg.png')",
            backgroundSize: "cover",
            backgroundPosition: {
              xs: "38% 9%",
              sm: "42% 9%",
              md: "center 9%",
            },
            bgcolor: "#EAEFF2",
          }}
        >
          <Container
            maxWidth="xl"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              px: 6,
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "38px", sm: "61px" },
                color: "#fff",
                fontWeight: 800,
                maxWidth: 800,
                lineHeight: 1.15,
                whiteSpace: "pre-line",
                letterSpacing: 0.2,
                textShadow: "0 3px 12px rgba(0,0,0,0.45)",
              }}
            >
              {t("contact.heroTitle")}
            </Typography>
          </Container>
        </Box>

        {/* Content Section */}
        <Container
          maxWidth="xl"
          sx={{
            px: { xs: 2, md: 6 },
            mx: "auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Grid
            container
            spacing={{ xs: 4, md: 6 }}
            alignItems="stretch"
            justifyContent="center"
            sx={{
              maxWidth: "1200px", // keeps layout centered on large screens
            }}
          >
            {/* Form Section */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={6}
                sx={{
                  bgcolor: "#F6F8F8",
                  border: "1px solid #E0E0E0",
                  borderRadius: 5,
                  p: { xs: 3, md: 4 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Box component="form" onSubmit={onSubmit} noValidate>
                  <TextField
                    label={t("contact.form.name")}
                    fullWidth
                    margin="normal"
                    value={form.name}
                    onChange={handleChange("name")}
                    onBlur={handleBlur("name")}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    required
                    InputProps={{ sx: { bgcolor: "#fff", borderRadius: 2 } }}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                      <TextField
                        label={t("contact.form.email")}
                        type="email"
                        fullWidth
                        margin="normal"
                        value={form.email}
                        onChange={handleChange("email")}
                        onBlur={handleBlur("email")}
                        error={Boolean(errors.email)}
                        helperText={errors.email}
                        required
                        InputProps={{
                          sx: { bgcolor: "#fff", borderRadius: 2 },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        label={t("contact.form.phone")}
                        fullWidth
                        margin="normal"
                        value={form.phone}
                        onChange={handleChange("phone")}
                        onBlur={handleBlur("phone")}
                        error={Boolean(errors.phone)}
                        helperText={errors.phone}
                        InputProps={{
                          sx: { bgcolor: "#fff", borderRadius: 2 },
                        }}
                      />
                    </Grid>
                  </Grid>
                  <TextField
                    label={t("contact.form.message")}
                    fullWidth
                    multiline
                    minRows={5}
                    margin="normal"
                    value={form.message}
                    onChange={handleChange("message")}
                    onBlur={handleBlur("message")}
                    error={Boolean(errors.message)}
                    helperText={errors.message}
                    required
                    InputProps={{ sx: { bgcolor: "#fff", borderRadius: 2 } }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: dir === "rtl" ? "flex-start" : "flex-end",
                      mt: 2,
                    }}
                  >
                    <Button
                      type="submit"
                      disabled={submitting}
                      variant="contained"
                      sx={{
                        bgcolor: "#F2C200",
                        color: "#000",
                        fontWeight: 700,
                        borderRadius: 6,
                        px: 4,
                        py: 1.2,
                        textTransform: "none",
                        "&:hover": { bgcolor: "#E5B800" },
                      }}
                    >
                      {t("contact.form.submit")}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Contact Info Section */}
            <Grid item xs={12} md={6}>
              <Stack
                spacing={3}
                justifyContent="center"
                sx={{ height: "100%" }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: "#F2C200",
                    fontWeight: 800,
                    textAlign: dir === "rtl" ? "right" : "left",
                    fontSize: { xs: "1.9rem", sm: "2.7rem" },
                  }}
                >
                  {t("contact.helpHeadline")}
                </Typography>

                <Box
                  sx={{
                    width: 96,
                    height: 96,
                    borderRadius: "50%",
                    bgcolor: "#F2C200",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PhoneOutlined sx={{ fontSize: 44, color: "#000" }} />
                </Box>
                <Stack spacing={1.5}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <EmailOutlined sx={{ color: "text.secondary" }} />
                    <Typography
                      component="a"
                      href={`mailto:${contactEmail}`}
                      fontWeight="bold"
                      fontSize="18px"
                      sx={{
                        color: "inherit",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {contactEmail}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <PhoneOutlined sx={{ color: "text.secondary" }} />
                    <Typography
                      component="a"
                      href={`tel:${whatsappNumber.replace(/[^\d+]/g, "")}`}
                      fontWeight="bold"
                      fontSize="18px"
                      sx={{
                        direction: "ltr",
                        unicodeBidi: "plaintext",
                        color: "inherit",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {whatsappNumber}
                    </Typography>
                  </Box>
                </Stack>

                <Button
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<WhatsApp />}
                  variant="contained"
                  sx={{
                    gap: 1.5,
                    bgcolor: "#25D366",
                    color: "#fff",
                    fontWeight: 700,
                    width: "fit-content",
                    borderRadius: 6,
                    px: 2.5,
                    py: 1.2,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#1EBE5B" },
                  }}
                >
                  {t("contact.whatsappCta")}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}
